import { getPlural } from "../string/getPlural";
import { calculateTimeUnits } from "./calculateTimeUnits";
import { getRoundedSingleUnit } from "./getRoundedSingleUnit";
import {
  DAYS_PER_MONTH,
  HOURS_PER_DAY,
  MONTHS_PER_YEAR,
  ROUNDING_THRESHOLDS,
} from "./time.constants";
import type { TimeUnits } from "./time.types";

// Time unit limits
const TIME_LIMITS = {
  HOURS_PER_DAY,
  DAYS_PER_MONTH,
  MONTHS_PER_YEAR,
  SINGLE_DIGIT_THRESHOLD: 10,
  MIN_HOURS_FOR_PLURALIZATION: 1,
} as const;

/**
 * Helper function to handle unit overflow when rounding up
 * @param value - Current value to check for overflow
 * @param limit - Maximum value before overflow
 * @returns Object with adjusted value and overflow flag
 */
const handleUnitOverflow = (value: number, limit: number) => ({
  value: value % limit,
  hasOverflow: value >= limit,
});

/**
 * Convert minutes to a human-readable duration string
 * @param minutes - Minutes to convert to a readable duration
 * @param round - If true, show only the most significant time unit with rounding
 * @returns human-readable duration string
 */
export function getReadableDurationFromMinutes(
  minutes?: string | number,
  round?: boolean
) {
  // Zero is valid — empty duration string (callers like "Last " + duration)
  if (
    minutes != null &&
    minutes !== "" &&
    !Number.isNaN(Number(minutes)) &&
    +minutes === 0
  ) {
    return "";
  }
  const units = calculateTimeUnits(minutes);
  if (!units) return;
  return getReadableDuration(units, round);
}

/**
 * Apply intelligent rounding logic to provide more intuitive duration displays
 * @param units - Time units object to convert to a readable duration
 * @param round - If true, show only the most significant time unit with rounding
 * @returns human-readable duration string
 */
export function getReadableDuration(units: TimeUnits, round?: boolean) {
  let { years, months, days, hours, minutes: mins } = units;

  // If round is true, return only the most significant unit with rounding
  if (round) {
    return getRoundedSingleUnit({
      years,
      months,
      days,
      hours,
      minutes: mins,
      seconds: units.seconds,
    });
  }

  // Round up hours if minutes are at or past halfway point
  if (mins >= ROUNDING_THRESHOLDS.MINUTES_TO_HOUR) {
    hours += 1;
    mins = 0; // Reset minutes when rounding up to hours
    const hourOverflow = handleUnitOverflow(hours, TIME_LIMITS.HOURS_PER_DAY);
    if (hourOverflow.hasOverflow) {
      days += 1;
      hours = hourOverflow.value;
    }
  }

  // Round up days if hours are at or past halfway point
  if (hours >= ROUNDING_THRESHOLDS.HOURS_TO_DAY) {
    days += 1;
    hours = 0; // Reset hours when rounding up to days
    const dayOverflow = handleUnitOverflow(days, TIME_LIMITS.DAYS_PER_MONTH);
    if (dayOverflow.hasOverflow) {
      months += 1;
      days = dayOverflow.value;
    }
  }

  // Round up months if days are at or past halfway point (consistent with useReadableTimeWindow)
  if (days >= ROUNDING_THRESHOLDS.DAYS_TO_MONTH) {
    // First handle any day overflow to months
    const dayOverflow = handleUnitOverflow(days, TIME_LIMITS.DAYS_PER_MONTH);
    if (dayOverflow.hasOverflow) {
      months += Math.floor(days / TIME_LIMITS.DAYS_PER_MONTH);
      days = dayOverflow.value;
    }

    // Then apply rounding if remaining days still meet threshold
    if (days >= ROUNDING_THRESHOLDS.DAYS_TO_MONTH) {
      months += 1;
      days = 0;
    }

    // Handle month overflow to years
    const monthOverflow = handleUnitOverflow(months, TIME_LIMITS.MONTHS_PER_YEAR);
    if (monthOverflow.hasOverflow) {
      years += 1;
      months = monthOverflow.value;
    }
  }

  if (years > 0) {
    const yearPart = `${years} ${getPlural(years, "year")}`;
    if (months > 0) {
      return `${yearPart} ${months} ${getPlural(months, "month")}`;
    }
    return yearPart;
  }

  if (months > 0) {
    const monthPart = `${months} ${getPlural(months, "month")}`;
    if (days > 0) {
      return `${monthPart} ${days} ${getPlural(days, "day")}`;
    }
    return monthPart;
  }

  if (days > 0) {
    const dayPart = `${days} ${getPlural(days, "day")}`;
    if (hours > 0) {
      return `${dayPart} ${hours} ${getPlural(hours, "hour")}`;
    }
    return dayPart;
  }

  if (hours < TIME_LIMITS.HOURS_PER_DAY) {
    if (hours > TIME_LIMITS.MIN_HOURS_FOR_PLURALIZATION) {
      return `${hours} ${getPlural(hours, "hour")}`;
    }
    return "1 hour";
  }

  const paddedMinutes =
    mins < TIME_LIMITS.SINGLE_DIGIT_THRESHOLD ? `0${mins}` : mins;
  const paddedSeconds =
    units.seconds < TIME_LIMITS.SINGLE_DIGIT_THRESHOLD
      ? `0${units.seconds}`
      : units.seconds;

  return `${hours}:${paddedMinutes}:${paddedSeconds}`;
}
